import {
    requestJson,
    RequestResponse,
} from '@vfde-sails/utils';
import {
    StrictEffect,
    call,
    put,
    takeLatest,
} from 'redux-saga/effects';
import {
    ActionCreators,
    actionCreators,
} from './action';
import {
    ACTION_GET_CAPTCHA,
    ACTION_TERMINATE_CONTRACT,
    CAPTCHA_BLOB,
    CONTAINER_CANCELLATION_FORM,
    CONTAINER_RESULT_PAGE,
    GET_CAPTCHA_API,
    HEADER_CAPTCHA,
    HEADER_CAPTCHA_ACCEPT_LANGUAGE,
    HEADER_CAPTCHA_ACCEPT_LANGUAGE_DE,
    HEADER_CAPTCHA_API_KEY,
    TERMINATE_CONTRACT_API,
    X_API_KEY,
} from './constant';
import { showOrHideBlock } from './helper/showOrHideBlock.helper';
import {
    ICustomerInformation,
    IError,
} from './interface';
import { successTracking } from './helper/tracking.helper';
import { SagaIterator } from 'redux-saga';

/**
 * send request to dxl terminate contract endpoint
 * with the customerinformation and the captcha
 */
export function* terminateContractSaga (payload: ReturnType<ActionCreators['terminateContract']>): Generator<StrictEffect> {
    const { customerInformation, captcha } = payload;
    const { terminateContractSuccess, terminateContractError } = actionCreators;

    try {
        const headers = {
            ...captcha,
            [HEADER_CAPTCHA_API_KEY]: X_API_KEY,
            'Content-Type': 'application/json',
        };
        const body = JSON.stringify(customerInformation);

        const options = {
            method: 'POST',
            headers,
            body,
        };

        const response = (yield call(requestJson, TERMINATE_CONTRACT_API, options)) as RequestResponse<ICustomerInformation>;

        if (response.body !== null) {
            yield put(terminateContractSuccess(response.body));
            successTracking();
            showOrHideBlock(CONTAINER_CANCELLATION_FORM, true);
            showOrHideBlock(CONTAINER_RESULT_PAGE, false);
            window.scrollTo(0, 0);
        }
    }
    catch (error) {
        yield put(terminateContractError(error as IError));
    }
}

/**
 * send a get request for a captcha image
 */
export function* getCaptchaSaga (payload: ReturnType<ActionCreators['getCaptcha']>): SagaIterator {
    const { captchaMediaType } = payload;
    const { getCaptchaSuccess, getCaptchaError } = actionCreators;

    try {
        const headers = {
            Accept: captchaMediaType, // eslint-disable-line @typescript-eslint/naming-convention
            [HEADER_CAPTCHA_API_KEY]: X_API_KEY,
            [HEADER_CAPTCHA_ACCEPT_LANGUAGE]: HEADER_CAPTCHA_ACCEPT_LANGUAGE_DE,
        };
        const optionsForImage = {
            headers,
            method: 'GET',
        };
        // sails core request won't work here because we cannot parse the reponse
        const captcha: Response = yield call(fetch, GET_CAPTCHA_API, optionsForImage);
        const blob: Blob = yield call([captcha, CAPTCHA_BLOB]);
        const url = URL.createObjectURL(blob);
        const captchaHash = captcha.headers.get(HEADER_CAPTCHA);

        yield put(getCaptchaSuccess(url, captchaMediaType, captchaHash!));
    }
    catch (error) {
        yield put(getCaptchaError(error as IError));
    }
}

/**
 * Action dispatcher for captcha and terminateContract
 */
export default function* cancellationFormSaga () {
    yield takeLatest(ACTION_TERMINATE_CONTRACT, terminateContractSaga);
    yield takeLatest(ACTION_GET_CAPTCHA, getCaptchaSaga);
}
